import { useEffect, useState, useMemo } from "react";

/**
 * Fireflies - Fixed-position layer with slowly drifting glowing dots.
 * Uses CSS animations + randomized delays; lightweight, no external deps.
 */
export const Fireflies = ({ count = 22 }) => {
  const flies = useMemo(() => {
    return Array.from({ length: count }).map((_, i) => {
      const size = 2 + Math.random() * 4;
      return {
        id: i,
        left: Math.random() * 100,
        top: Math.random() * 100,
        size,
        delay: Math.random() * 5,
        duration: 14 + Math.random() * 18,
        driftX: (Math.random() - 0.5) * 200,
        driftY: (Math.random() - 0.5) * 220,
      };
    });
  }, [count]);

  return (
    <div
      aria-hidden="true"
      data-testid="fireflies-layer"
      style={{
        position: "fixed",
        inset: 0,
        pointerEvents: "none",
        zIndex: 2,
        overflow: "hidden",
      }}
    >
      {flies.map((f) => (
        <span
          key={f.id}
          className="firefly"
          style={{
            left: `${f.left}%`,
            top: `${f.top}%`,
            width: `${f.size}px`,
            height: `${f.size}px`,
            animationDelay: `${f.delay}s`,
            // Custom keyframes via inline style for drift
            transform: `translate(0,0)`,
            animation: `firefly-pulse 3s ease-in-out infinite, drift-${f.id} ${f.duration}s ease-in-out infinite`,
          }}
        />
      ))}
      {/* Inject unique drift keyframes for organic motion */}
      <style>
        {flies
          .map(
            (f) => `
            @keyframes drift-${f.id} {
              0%   { transform: translate(0px, 0px); }
              25%  { transform: translate(${f.driftX * 0.4}px, ${f.driftY * 0.6}px); }
              50%  { transform: translate(${f.driftX}px, ${f.driftY}px); }
              75%  { transform: translate(${f.driftX * 0.5}px, ${f.driftY * 0.2}px); }
              100% { transform: translate(0px, 0px); }
            }
          `,
          )
          .join("\n")}
      </style>
    </div>
  );
};

/**
 * ScrollParticles - Gold light dots that appear as the user scrolls.
 * Very lightweight: single fixed layer sampling scrollY.
 */
export const ScrollParticles = ({ count = 14 }) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement;
      const p =
        h.scrollTop / Math.max(1, h.scrollHeight - h.clientHeight || 1);
      setProgress(p);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const particles = useMemo(
    () =>
      Array.from({ length: count }).map((_, i) => ({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: 1 + Math.random() * 3,
        threshold: Math.random(),
      })),
    [count],
  );

  return (
    <div
      aria-hidden="true"
      data-testid="scroll-particles-layer"
      style={{
        position: "fixed",
        inset: 0,
        pointerEvents: "none",
        zIndex: 2,
        overflow: "hidden",
      }}
    >
      {particles.map((p) => {
        const active = progress > p.threshold;
        return (
          <span
            key={p.id}
            style={{
              position: "absolute",
              left: `${p.x}%`,
              top: `${p.y}%`,
              width: `${p.size}px`,
              height: `${p.size}px`,
              borderRadius: "50%",
              background: "#D4AF37",
              boxShadow:
                "0 0 8px 2px rgba(212,175,55,0.55), 0 0 18px 6px rgba(212,175,55,0.25)",
              opacity: active ? 0.9 : 0,
              transform: active ? "translateY(0)" : "translateY(20px)",
              transition:
                "opacity 1.2s ease, transform 1.4s cubic-bezier(0.22, 1, 0.36, 1)",
            }}
          />
        );
      })}
    </div>
  );
};
